import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { Play, Clock, Building2, Check, AlertTriangle, Calendar, Truck, Package, FileWarning, Inbox, Footprints, UserCheck, MessageSquare, BookOpen, ClipboardCheck, ShieldCheck, CalendarRange, ArrowRight, PartyPopper, ShoppingCart, LogIn, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { saludoMendoza, relojMendoza, PATROL_STATUS_LABEL } from "@/lib/i18n";
import { ActiveVisitsPanel } from "@/components/ActiveVisitsPanel";
import { CallSupervisorButton } from "@/components/CallSupervisorButton";
import { InstallPwaButton } from "@/components/InstallPwaButton";
import { SiteSelect } from "@/components/SiteSelect"; // Fix BK

const STATUS_BADGE = {
  pendiente: "gray", en_curso: "blue", completada: "green", incompleta: "red", cancelada: "gray",
};
// build-invalidation: call-supervisor-2026-07-21

function todayKey() { return new Date().toISOString().slice(0, 10); }
function isToday(iso) { return iso?.slice(0, 10) === todayKey(); }
function isFuture(iso) { return iso && iso > new Date().toISOString(); }

export default function VigiladorHome() {
  const { user } = useAuth();
  const [patrols, setPatrols] = useState(null);
  const [tab, setTab] = useState("today");
  const [activeVisits, setActiveVisits] = useState(null);
  const [supervisorContact, setSupervisorContact] = useState(null);
  const [sumToday, setSumToday] = useState(null);
  const [opsTasksAvailable, setOpsTasksAvailable] = useState(false);
  const [servicePrompts, setServicePrompts] = useState([]);
  const [promptSites, setPromptSites] = useState({}); // Fix BK: sede elegida para turnos que no la traen
  const [myOpenServices, setMyOpenServices] = useState([]);
  const [serviceBusy, setServiceBusy] = useState(null);

  const loadServices = async () => {
    try {
      const [{ data: prompts }, { data: open }] = await Promise.all([
        api.get("/services/prompt"),
        api.get("/services", { params: { status: "open" } }),
      ]);
      setServicePrompts(prompts || []);
      setMyOpenServices((open || []).filter((s) => s.supervisor_id === user?.id));
    } catch { /* silencioso: no bloquear el home por esto */ }
  };

  useEffect(() => { loadServices(); }, []);

  const openService = async (t, takeover = false) => {
    setServiceBusy(`${t.client_id}-${t.site_id || ""}`);
    try {
      const site_id = t.site_id || promptSites[`${t.client_id}-`] || null; // Fix BK
      await api.post("/services/open", { client_id: t.client_id, site_id, takeover });
      toast.success(takeover ? `Relevo tomado en ${t.client_name}` : `Servicio abierto en ${t.client_name}`);
      await loadServices();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "service_already_open") {
        if (window.confirm(`${detail.msg}\n\n¿Tomar el relevo ahora?`)) return openService(t, true);
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setServiceBusy(null); }
  };

  const closeService = async (s) => {
    setServiceBusy(s.id);
    try {
      await api.post("/services/close", { service_id: s.id });
      toast.success("Servicio cerrado");
      await loadServices();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setServiceBusy(null); }
  };

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/patrols/history"); setPatrols(data); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/panic/supervisor-contact"); setSupervisorContact(data); }
      catch { setSupervisorContact({ supervisor_phone: null, supervisor_name: null }); }
    })();
  }, []);

  useEffect(() => {
    const loadActive = async () => {
      try { const { data } = await api.get("/visits/active"); setActiveVisits(data); }
      catch { /* silencioso: no bloquear el home por esto */ }
    };
    loadActive();
    const iv = setInterval(loadActive, 40000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/sum-reservations/today-mine"); setSumToday(data); }
      catch { /* silencioso: no bloquear el home por esto */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/clients"); setOpsTasksAvailable((data || []).some((c) => c.ops_tasks_enabled)); }
      catch { /* silencioso: no bloquear el home por esto */ }
    })();
  }, []);

  if (patrols == null) return <div style={{ padding: 16 }}><div className="skel" style={{ height: 120 }} /></div>;

  const today = patrols.filter((p) => isToday(p.scheduled_for) && ["pendiente", "en_curso"].includes(p.status));
  const upcoming = patrols.filter((p) => isFuture(p.scheduled_for) && !isToday(p.scheduled_for) && p.status === "pendiente");
  const past = patrols.filter((p) => ["completada", "incompleta", "cancelada"].includes(p.status) || (!isToday(p.scheduled_for) && !isFuture(p.scheduled_for)));

  const list = tab === "today" ? today : tab === "upcoming" ? upcoming : past;

  // Week stats for hero
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const weekDone = patrols.filter((p) => p.status === "completada" && p.completed_at && p.completed_at > weekAgo).length;
  const weekAnomalies = patrols.filter((p) => p.completed_at && p.completed_at > weekAgo).reduce((s, p) => s + (p.anomalies || 0), 0);

  return (
    <div style={{ padding: "8px 12px 0" }}>
      <div style={{ background: "linear-gradient(135deg, #0A0A0A 0%, #1a0e0f 100%)", color: "#FFFBF4", padding: "22px 18px", borderRadius: 4, marginBottom: 14, position: "relative", overflow: "hidden" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.22em", color: "#BF3437", fontWeight: 800, textTransform: "uppercase", marginBottom: 6 }}>Vigilador · Mendoza · {relojMendoza()}</div>
        <h1 style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.15, margin: 0, letterSpacing: "-0.02em" }}>
          {saludoMendoza()}, <span style={{ color: "#BF3437" }}>{(user?.name || "").split(" ")[0] || "vigilador"}</span>.
        </h1>
        <div style={{ display: "flex", gap: 22, marginTop: 16, position: "relative", zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 9, color: "rgba(255,251,244,0.5)", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700 }}>Esta semana</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#FFFBF4" }}>{weekDone}<span style={{ fontSize: 11, color: "rgba(255,251,244,0.5)", marginLeft: 4 }}>completadas</span></div>
          </div>
          {weekAnomalies > 0 && (
            <div>
              <div style={{ fontSize: 9, color: "rgba(255,251,244,0.5)", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700 }}>Anomalías</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#BF3437" }}>{weekAnomalies}</div>
            </div>
          )}
        </div>
        <div style={{ position: "absolute", right: -50, bottom: -20, width: 130, height: 130, background: "radial-gradient(circle, rgba(191,52,55,0.18), transparent 70%)" }} />
      </div>

      <InstallPwaButton />

      <Link to="/vigilador/visitas" className="card" data-testid="vig-ingreso"
        style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, marginBottom: 14, textDecoration: "none", color: "inherit", borderLeft: "4px solid #BF3437" }}>
        <div style={{ width: 42, height: 42, borderRadius: 8, background: "rgba(191,52,55,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Footprints size={20} style={{ color: "#BF3437" }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 900, fontSize: 14, color: "#0A0A0A" }}>Cargar ingreso</div>
          <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }}>Registrar entrada o salida de una visita</div>
        </div>
        <ArrowRight size={18} style={{ color: "var(--invict-text-muted)", flexShrink: 0 }} />
      </Link>

      <ActiveVisitsPanel data={activeVisits} onChanged={async () => {
        try { const { data } = await api.get("/visits/active"); setActiveVisits(data); } catch { /* silencioso */ }
      }} />

      {(myOpenServices.length > 0 || servicePrompts.length > 0) && (
        <div className="card" style={{ marginBottom: 14, padding: 14, borderLeft: "4px solid #16a34a" }} data-testid="vig-services-panel">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <ShieldCheck size={16} style={{ color: "#16a34a" }} />
            <div style={{ fontWeight: 900, fontSize: 13, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.04em" }}>Servicio de turno</div>
          </div>
          {myOpenServices.map((s) => (
            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "8px 0", borderTop: "1px solid var(--invict-bone-3)" }} data-testid={`vig-open-service-${s.id}`}>
              <div style={{ fontSize: 12 }}>
                <span className="badge green" style={{ fontSize: 9, marginRight: 6 }}>Abierto</span>
                {s.site_name ? `${s.site_name}` : "Servicio activo"} · desde {new Date(s.opened_at).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
              </div>
              <button onClick={() => closeService(s)} disabled={serviceBusy === s.id} className="btn-outline" style={{ padding: "6px 10px", fontSize: 11, flexShrink: 0 }} data-testid={`vig-close-service-${s.id}`}>
                <LogOut size={12} style={{ marginRight: 4 }} />{serviceBusy === s.id ? "Cerrando…" : "Cerrar servicio"}
              </button>
            </div>
          ))}
          {servicePrompts.map((t) => {
            const busyKey = `${t.client_id}-${t.site_id || ""}`;
            return (
              <div key={busyKey} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "8px 0", borderTop: "1px solid var(--invict-bone-3)" }} data-testid={`vig-service-prompt-${busyKey}`}>
                <div style={{ fontSize: 12, flex: 1 }}>
                  {t.time_from ? `Turno ${t.time_from}–${t.time_to} en ` : ""}<b>{t.client_name}</b>{t.site_name ? ` · ${t.site_name}` : ""}
                  {/* Fix BK: si el objetivo tiene sedes y el turno no trae la sede, se elige acá (SiteSelect no se muestra si no hay sedes). */}
                  {!t.site_id && (
                    <SiteSelect clientId={t.client_id} value={promptSites[busyKey] || ""} required label="Sede donde estás *"
                      onChange={(v) => setPromptSites((p) => ({ ...p, [busyKey]: v }))} testIdPrefix={`vig-service-${busyKey}`} />
                  )}
                </div>
                <button onClick={() => openService(t)} disabled={serviceBusy === busyKey} className="btn-primary" style={{ padding: "6px 10px", fontSize: 11, flexShrink: 0 }} data-testid={`vig-open-service-btn-${busyKey}`}>
                  <LogIn size={12} style={{ marginRight: 4 }} />{serviceBusy === busyKey ? "Abriendo…" : "Abrir servicio"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {sumToday?.length > 0 && (
        <div className="card" style={{ marginBottom: 14, padding: 14, borderLeft: "4px solid #d97706" }} data-testid="vig-sum-today-panel">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <PartyPopper size={16} style={{ color: "#d97706" }} />
            <div style={{ fontWeight: 900, fontSize: 13, color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "0.04em" }}>Reservas del SUM hoy</div>
          </div>
          {sumToday.map((r) => (
            <div key={r.id} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 0", borderTop: "1px solid var(--invict-bone-3)" }} data-testid={`vig-sum-today-${r.id}`}>
              <div style={{ fontSize: 12 }}>
                <b>{r.time_from}–{r.time_to}</b> · Unidad {r.unit} · {r.guests_count} invitados{r.reason ? ` · ${r.reason}` : ""}
              </div>
              <span className={`badge ${r.status === "confirmada" ? "green" : "amber"}`} style={{ fontSize: 9, flexShrink: 0 }}>{r.status === "confirmada" ? "Confirmada" : "A confirmar"}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 14, background: "var(--invict-bone-2)", padding: 4, borderRadius: 3 }}>
        {[
          { k: "today", label: `Hoy (${today.length})` },
          { k: "upcoming", label: `Próximas (${upcoming.length})` },
          { k: "past", label: `Pasadas (${past.length})` },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{ flex: 1, padding: "10px", border: "none", background: tab === t.k ? "white" : "transparent", color: tab === t.k ? "#0A0A0A" : "var(--invict-text-muted)", fontWeight: 800, fontSize: 11, letterSpacing: "0.05em", textTransform: "uppercase", borderRadius: 2, cursor: "pointer", boxShadow: tab === t.k ? "0 1px 4px rgba(0,0,0,0.05)" : "none" }}
            data-testid={`tab-${t.k}`}
          >{t.label}</button>
        ))}
      </div>

      {list.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: 28 }}>
          <Calendar size={28} style={{ color: "var(--invict-text-muted)", margin: "0 auto 8px" }} />
          <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>
            {tab === "today" ? "Sin rondas asignadas hoy" : tab === "upcoming" ? "Sin rondas próximas" : "Sin historial"}
          </div>
          <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>
            {tab === "today" ? "Te avisaremos en cuanto haya una nueva." : tab === "upcoming" ? "No tenés rondas programadas para los próximos días." : "Tus rondas pasadas aparecerán acá."}
          </div>
        </div>
      )}

      {list.map((p) => {
        const isActive = ["pendiente", "en_curso"].includes(p.status);
        const target = isActive ? `/vigilador/ronda/${p.id}` : `/vigilador/ronda-vista/${p.id}`;
        return (
          <Link key={p.id} to={target} className="card" style={{ padding: 16, marginBottom: 10, display: "block", textDecoration: "none", color: "inherit", borderLeft: `4px solid ${isActive ? "#BF3437" : "var(--invict-bone-3)"}` }} data-testid={`patrol-card-${p.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, letterSpacing: "0.2em", color: isActive ? "#BF3437" : "var(--invict-text-muted)", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>{PATROL_STATUS_LABEL[p.status] || p.status}</div>
                <div style={{ fontWeight: 900, fontSize: 18, color: "#0A0A0A", lineHeight: 1.1 }}><Building2 size={16} style={{ display: "inline", marginRight: 6, color: "var(--invict-text-muted)" }} />{p.client_name}{p.site_name ? ` · ${p.site_name}` : ""}</div>
              </div>
              <span className={`badge ${STATUS_BADGE[p.status]}`}>{p.points_completed}/{p.total_points}</span>
            </div>

            <div style={{ display: "flex", gap: 14, fontSize: 11, color: "var(--invict-text-muted)", marginBottom: isActive ? 12 : 0 }}>
              <span><Clock size={11} style={{ display: "inline", marginRight: 4 }}/>{new Date(p.scheduled_for).toLocaleString("es-AR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}</span>
              {p.anomalies > 0 && <span style={{ color: "#BF3437" }}><AlertTriangle size={11} style={{ display: "inline", marginRight: 3 }}/>{p.anomalies} anomalía(s)</span>}
            </div>

            {isActive && (
              <button className={`btn-primary ${p.status === "en_curso" ? "pulse-red" : ""}`} style={{ width: "100%", padding: "12px", fontSize: 12 }} data-testid={`patrol-start-${p.id}`}>
                <Play size={12} style={{ marginRight: 6, fill: "currentColor" }} />
                {p.status === "en_curso" ? "Continuar ronda" : "Iniciar ronda"}
              </button>
            )}
            {!isActive && p.status === "completada" && (
              <div style={{ marginTop: 8, fontSize: 11, color: "#16a34a", fontWeight: 700 }}>
                <Check size={11} style={{ display: "inline", marginRight: 4 }}/>Completada {p.completed_at ? new Date(p.completed_at).toLocaleString("es-AR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : ""}
              </div>
            )}
          </Link>
        );
      })}

      {/* Contacto directo: llamar al supervisor sin necesidad de disparar un SOS */}
      <div className="card" data-testid="vig-call-supervisor-card"
        style={{ padding: 14, marginTop: 16, background: "#0A0A0A", border: "1px solid rgba(191,52,55,0.35)" }}>
        <div style={{ fontSize: 10, letterSpacing: "0.16em", color: "rgba(255,251,244,0.55)", fontWeight: 800, textTransform: "uppercase", marginBottom: 8 }}>
          Contacto directo
        </div>
        <CallSupervisorButton
          phone={supervisorContact?.supervisor_phone}
          supervisorName={supervisorContact?.supervisor_name}
          loading={supervisorContact === null}
          variant="quick"
        />
      </div>

      {/* Quick actions for vigilador */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 16 }}>
        <Link to="/vigilador/novedad" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-novedad">
          <FileWarning size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Novedad</div>
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Cargar incidente</div>
        </Link>
        <Link to="/vigilador/solicitud" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-solicitud">
          <Inbox size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Solicitud</div>
          {/* Fix AY (2026-09-30): subtítulo con los tipos que existen hoy (el de sueldo se sacó en AA). */}
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Materiales, compras</div>
        </Link>
        <Link to="/vigilador/vehiculos" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-vehiculos">
          <Truck size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Vehículos</div>
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Entrada / salida</div>
        </Link>
        <Link to="/vigilador/visitas" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-visitas">
          <Footprints size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Visitas</div>
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Peatonales</div>
        </Link>
        <Link to="/vigilador/personal" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-personal">
          <UserCheck size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Autorizados</div>
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Personal habilitado</div>
        </Link>
        <Link to="/vigilador/materiales" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-materiales">
          <Package size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
          {/* Fix AP (2026-09-25): pedido del cliente, este acceso se llama CHECK LIST. */}
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>CHECK LIST</div>
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Elementos del turno</div>
        </Link>
        <Link to="/vigilador/elementos" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-elementos">
          <ClipboardCheck size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Elementos de Trabajo</div>
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Kit personal · Herramientas del cliente</div>
        </Link>
        <Link to="/vigilador/chats" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-chats">
          <MessageSquare size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Chats</div>
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Con tu equipo</div>
        </Link>
        <Link to="/vigilador/manual" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-manual">
          <BookOpen size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Manual</div>
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Procesos · Firmar</div>
        </Link>
        <Link to="/vigilador/guardias" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-guardias">
          <ShieldCheck size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Guardias Pasivas</div>
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Retén · Contacto</div>
        </Link>
        <Link to="/vigilador/cronograma" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-cronograma">
          <CalendarRange size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Cronograma</div>
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Mis turnos</div>
        </Link>
        {opsTasksAvailable && (
          <Link to="/vigilador/tareas-operativas" className="card" style={{ padding: 14, textDecoration: "none", color: "inherit", textAlign: "center" }} data-testid="vig-tareas-operativas">
            <ShoppingCart size={24} style={{ color: "#BF3437", margin: "0 auto 6px" }}/>
            <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>Tareas operativas</div>
            <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 2 }}>Tickets · Recuperos · Perfiles</div>
          </Link>
        )}
      </div>
    </div>
  );
}
